/**
 * One card of the hosted page profile editor: its own form, Save and
 * Discard, and the unsaved-changes guard (router.tsx) while it differs from
 * what the API last returned.
 *
 * A section works on a form model `F` (strings for inputs) derived from the
 * profile by `toForm`, and turns it back into the slice of the PUT body it
 * owns with `toBody`. Because every key of the PUT is optional and arrays
 * replace, a section sends only its own keys, so saving one card never
 * overwrites another card's unsaved edits. When the API answers with a
 * profile whose slice for this card changed (this save, or another card's
 * save that the server normalised), the form resets to it — during render,
 * like HostedPagePanel's editor, so an edit typed before an effect flushed is
 * never wiped.
 */
import { useId, useState, type ReactNode } from 'react';
import type { ApiError, FieldIssue } from '../api/errors';
import type { HostedPageProfileInput, HostedPageProfileResponse } from '../api/types';
import { useSession } from '../auth/session';
import { useUnsavedChangesGuard } from '../router';
import { ErrorNotice, useToast } from '../ui/kit';
import { useMutation } from '../ui/hooks';

export interface SectionApi<F> {
  form: F;
  set: (next: F) => void;
  /** Client-side issues for `path`, then the server's for the same path. */
  issues: (path: string) => FieldIssue[];
  disabled: boolean;
}

export const SAVED_NOTE = 'Saved. Changes show on your page within a minute.';

/** The server's dotted paths may or may not carry a `profile.` prefix. */
function samePath(a: string, b: string): boolean {
  const strip = (p: string) => p.replace(/^(body\.|profile\.)/, '');
  return strip(a) === strip(b);
}

export function serverIssues(error: ApiError | null, path: string): FieldIssue[] {
  return error?.fields.filter((f) => samePath(f.path, path)) ?? [];
}

export function ProfileSection<F>({
  id,
  title,
  description,
  slug,
  profile,
  toForm,
  toBody,
  validate,
  onSaved,
  children,
  aside,
  saveLabel,
}: {
  id: string;
  title: string;
  description?: ReactNode;
  slug: string;
  profile: HostedPageProfileInput;
  toForm: (profile: HostedPageProfileInput) => F;
  toBody: (form: F) => Partial<HostedPageProfileInput>;
  validate?: (form: F) => FieldIssue[];
  onSaved: (response: HostedPageProfileResponse) => void;
  children: (api: SectionApi<F>) => ReactNode;
  /** Shown under the form, outside it (a link elsewhere). */
  aside?: ReactNode;
  /** The Save button's words (default "Save " + the title in lower case). */
  saveLabel?: string;
}) {
  const { api } = useSession();
  const toast = useToast();
  const headingId = useId();
  const savedForm = toForm(profile);
  const savedKey = JSON.stringify(savedForm);
  const [form, setForm] = useState<F>(savedForm);
  const [seenKey, setSeenKey] = useState(savedKey);
  const save = useMutation((body: Partial<HostedPageProfileInput>) => api.updateHostedPageProfile(slug, body));
  if (seenKey !== savedKey) {
    setSeenKey(savedKey);
    setForm(savedForm);
  }
  const dirty = JSON.stringify(form) !== savedKey;
  useUnsavedChangesGuard(dirty);
  const clientIssues = dirty && validate ? validate(form) : [];
  const issues = (path: string) => [...clientIssues.filter((i) => i.path === path), ...serverIssues(save.error, path)];
  // A 400 whose paths no field claimed still says what was wrong.
  const unclaimed = save.error?.code === 'VALIDATION_FAILED' ? save.error.fields : [];

  return (
    <section id={id} className="panel panel-pad stack" aria-labelledby={headingId} tabIndex={-1}>
      <div className="stack-sm" style={{ gap: 4 }}>
        <h2 id={headingId} className="h-sub" style={{ margin: 0 }}>
          {title}
        </h2>
        {description ? (
          <p className="small muted" style={{ margin: 0 }}>
            {description}
          </p>
        ) : null}
      </div>
      <form
        className="stack"
        aria-label={title}
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          if (!dirty || clientIssues.length) return;
          const saved = await save.run(toBody(form));
          if (saved) {
            onSaved(saved);
            toast(SAVED_NOTE);
          }
        }}
      >
        {children({ form, set: (next) => setForm(next), issues, disabled: save.pending })}
        <ErrorNotice error={save.error} />
        {unclaimed.length ? (
          <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
            {unclaimed.map((f, i) => (
              <li key={i}>{f.message}</li>
            ))}
          </ul>
        ) : null}
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={!dirty || save.pending || clientIssues.length > 0}>
            {save.pending ? 'Saving…' : (saveLabel ?? `Save ${title.toLowerCase()}`)}
          </button>
          {dirty ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setForm(savedForm);
                save.reset();
              }}
            >
              Discard
            </button>
          ) : null}
        </div>
      </form>
      {aside}
    </section>
  );
}
